// One strip under every editor's header: where the record stands, and the one or two things to do next
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { Save, Send, Upload } from "lucide-react"
import type { Person, ReviewRequest } from "../persistence/recordMeta"

export interface RecordStatus {
  // draft and published belong to records with a publish state (articles per language, services); live to the rest
  state: "draft" | "published" | "live"
  // The state as the record names it, e.g. "ready for translation"
  label?: string
  // Which language the state belongs to
  scope?: string
  // Saved edits that aren't on the public site yet
  pending: { summary: string } | null
  canPublish: boolean
  onPublishChanges: () => void
  onDiscardChanges: () => void
  onPublish?: () => void
  onUnpublish?: () => void
  // A request for someone to review the unpublished changes
  review?: ReviewRequest | null
  people?: Person[]
  onRequestReview?: (assignee: Person | undefined) => void
  onCancelReview?: () => void
}

export interface UnsavedEdits {
  onSave: () => void
  onDiscard: () => void
  changedElsewhere?: boolean
}

const TONE = {
  unsaved: "bg-amber-50 text-amber-950 dark:bg-amber-950/30 dark:text-amber-100",
  pending: "bg-sky-50 text-sky-950 dark:bg-sky-950/30 dark:text-sky-100",
  draft: "bg-muted/50 text-foreground",
  published: "bg-background text-foreground",
} as const

const DOT = {
  unsaved: "bg-amber-500",
  pending: "bg-sky-500",
  draft: "bg-muted-foreground/50",
  published: "bg-emerald-500",
} as const

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export function RecordStatusBar({ status, unsaved }: { status?: RecordStatus; unsaved?: UnsavedEdits | null }) {
  const [confirm, setConfirm] = useState<"discard" | "unpublish" | null>(null)
  // Choosing who reviews, before the request is sent
  const [requesting, setRequesting] = useState(false)
  const [reviewer, setReviewer] = useState("anyone")

  // Ctrl+S / Cmd+S saves, as people expect from an editor
  useEffect(() => {
    if (!unsaved) return
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault()
        unsaved.onSave()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [unsaved])

  const pending = status?.pending
  const review = pending ? status?.review : null
  const tone = unsaved ? "unsaved" : pending ? "pending" : status?.state === "draft" ? "draft" : "published"
  // Records without a publish state only need the strip when there's something to do
  if (!unsaved && !pending && (!status || status.state === "live")) return null

  const scope = status?.scope ? ` · ${status.scope}` : ""
  const stateName = status?.label ? capital(status.label) : status?.state === "draft" ? "Draft" : "Published"

  let title: string
  let detail: string
  // The longer explanation, on hover
  let hint = ""
  if (unsaved) {
    title = "Unsaved changes"
    detail = unsaved.changedElsewhere ? "Someone else saved this since you started" : "Save to keep them"
    hint = unsaved.changedElsewhere ? "Saving replaces the change they made." : "Ctrl+S saves."
  } else if (pending && review) {
    title = "Awaiting review"
    detail = `${review.assignee ? `Assigned to ${review.assignee.name}` : "Any manager can review"} · ${pending.summary}`
    hint = `Requested${review.requestedBy ? ` by ${review.requestedBy}` : ""} ${new Date(review.requestedAt).toLocaleString()}.`
  } else if (pending) {
    title = "Changes not live yet"
    detail = status?.canPublish ? pending.summary : `${pending.summary} · a manager publishes them`
    hint = "The public site and agents see the previous version until these are published."
  } else if (status?.state === "draft") {
    // The language bar just below names the language
    title = stateName
    detail = "Not on the public site"
  } else {
    title = stateName
    detail = "On the public site"
  }

  return (
    <div className={cn("flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-3 py-2", TONE[tone])} title={hint || undefined}>
      <span className={cn("h-2 w-2 shrink-0 rounded-full", DOT[tone])} />
      {/* When narrow, the buttons wrap under the text rather than cutting it off */}
      <div className="min-w-[9rem] flex-1 leading-tight">
        <p className="text-xs font-medium">{title}</p>
        {!confirm && !requesting && <p className="truncate text-[11px] opacity-70">{detail}</p>}
      </div>

      <div className="ml-auto flex shrink-0 flex-wrap items-center gap-1.5">
        {requesting ? (
          <>
            <Select value={reviewer} onValueChange={setReviewer}>
              <SelectTrigger className="h-7 w-40 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="anyone" className="text-xs">Any manager</SelectItem>
                {(status?.people ?? []).map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              size="sm" className="h-7 gap-1.5 px-2.5 text-xs"
              onClick={() => {
                setRequesting(false)
                status?.onRequestReview?.(status.people?.find(p => p.id === reviewer))
              }}
            >
              <Send className="h-3.5 w-3.5" /> Send
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2.5 text-xs" onClick={() => setRequesting(false)}>Cancel</Button>
          </>
        ) : confirm ? (
          <>
            <span className="text-xs">
              {confirm === "discard" ? "Discard these changes?" : `Take${scope.replace(" · ", " ")} off the public site?`}
            </span>
            <Button
              size="sm" variant="destructive" className="h-7 px-2.5 text-xs"
              onClick={() => {
                setConfirm(null)
                if (confirm === "discard") status?.onDiscardChanges()
                else status?.onUnpublish?.()
              }}
            >
              {confirm === "discard" ? "Discard" : "Unpublish"}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2.5 text-xs" onClick={() => setConfirm(null)}>Cancel</Button>
          </>
        ) : unsaved ? (
          <>
            <Button size="sm" variant="ghost" className="h-7 px-2.5 text-xs" onClick={unsaved.onDiscard}>Discard</Button>
            <Button size="sm" className="h-7 gap-1.5 px-3 text-xs" onClick={unsaved.onSave} title="Save (Ctrl+S)">
              <Save className="h-3.5 w-3.5" /> Save
            </Button>
          </>
        ) : pending ? (
          <>
            <Button size="sm" variant="ghost" className="h-7 px-2.5 text-xs" onClick={() => setConfirm("discard")}>Discard changes</Button>
            {review && !status?.canPublish && status?.onCancelReview && (
              <Button size="sm" variant="ghost" className="h-7 px-2.5 text-xs" onClick={status.onCancelReview}>Cancel request</Button>
            )}
            {/* Managers publish directly; everyone else asks one to */}
            {!review && !status?.canPublish && status?.onRequestReview && (
              <Button
                size="sm" className="h-7 gap-1.5 px-2.5 text-xs"
                onClick={() => { setReviewer("anyone"); setRequesting(true) }}
              >
                <Send className="h-3.5 w-3.5" /> Request review
              </Button>
            )}
            {status?.canPublish && (
              <Button size="sm" className="h-7 gap-1.5 px-3 text-xs" onClick={status.onPublishChanges}>
                <Upload className="h-3.5 w-3.5" /> Publish changes
              </Button>
            )}
          </>
        ) : status?.canPublish && status.state === "draft" && status.onPublish ? (
          <Button size="sm" className="h-7 gap-1.5 px-3 text-xs" onClick={status.onPublish}>
            <Upload className="h-3.5 w-3.5" /> Publish{scope ? ` ${status.scope}` : ""}
          </Button>
        ) : status?.canPublish && status.state === "published" && status.onUnpublish ? (
          <Button size="sm" variant="ghost" className="h-7 px-2.5 text-xs text-muted-foreground" onClick={() => setConfirm("unpublish")}>
            Unpublish
          </Button>
        ) : null}
      </div>
    </div>
  )
}
